import { Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { memo, useCallback, useState } from 'react';

import chart from '~/assets/icons/chart.svg';
import { ChartPopover } from '~/pages/Company/components/Inference/components/ChartPopover';
import { UnicornZoneScatter } from '~/pages/Company/components/Inference/components/UnicornZoneScatter';
import { GetCompanyInferenceResponse } from '~/services/rtkQuery/company/types';

import styles from './styles.module.scss';

const BARS = ['Non-Unicorn', 'Projected Unicorn', 'Unicorn Neutral'] as const;

interface Props {
  askingPrice: number;
  pcaX: number;
  pcaY: number;
  uzXTh: number;
  data: GetCompanyInferenceResponse['result']['data']['unicorn_zone'];
  scatterChartData: GetCompanyInferenceResponse['result']['data']['supporting_graphs']['unicorn_zone'];
}

const Component: React.FC<Props> = ({ askingPrice, pcaX, pcaY, uzXTh, scatterChartData, data }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleChartButtonClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleChartPopoverClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const renderBar = useCallback(
    (bar: (typeof BARS)[number], index: number) => {
      const nonUnicornActive = data.graph_mode === false && bar === 'Non-Unicorn';
      const projectedUnicornActive = data.graph_mode === true && bar === 'Projected Unicorn';
      const neutralUnicornActive = data.graph_mode === 'neutral' && bar === 'Unicorn Neutral';

      return (
        <div
          className={clsx(styles.chart__bar, {
            [styles['chart__bar--last']]: index === BARS.length - 1,
            [styles['chart__bar--non']]: nonUnicornActive,
            [styles['chart__bar--projected']]: projectedUnicornActive,
            [styles['chart__bar--neutral']]: neutralUnicornActive,
          })}
          key={index}
        >
          {(nonUnicornActive || projectedUnicornActive || neutralUnicornActive) && (
            <>
              <Typography
                align="center"
                color="var(--text-3-color)"
                fontWeight="600"
                lineHeight="22px"
                variant="h6"
              >
                {bar}
              </Typography>
              <Typography
                align="center"
                color="var(--text-3-color)"
                fontWeight="600"
                lineHeight="22px"
                mb="16px"
                variant="body1"
              >
                Asking price ${askingPrice.toFixed(0)}M
              </Typography>
            </>
          )}
          <Typography align="center" color="var(--gray-2-color)" variant="subtitle2">
            {bar}
          </Typography>
        </div>
      );
    },
    [askingPrice, data.graph_mode],
  );

  return (
    <div className={styles.container}>
      <div className={styles.container__heading}>
        <Typography color="var(--text-3-color)" fontWeight="500" variant="subtitle1">
          Unicorn Zone
        </Typography>
        <button type="button" onClick={handleChartButtonClick}>
          <img alt="chart" src={chart} />
        </button>
        <ChartPopover anchorEl={anchorEl} onClose={handleChartPopoverClose}>
          <UnicornZoneScatter
            className={styles['container__scatter-chart']}
            data={scatterChartData}
            pcaX={pcaX}
            pcaY={pcaY}
            uzXTh={uzXTh}
          />
        </ChartPopover>
      </div>
      <div className={styles.chart}>
        <div className={styles.chart__bars}>{BARS.map(renderBar)}</div>
        <div className={styles['chart__x-axis-line']} />
        <Typography
          align="center"
          bottom="-26px"
          color="var(--text-3-color)"
          fontWeight="700"
          left="calc(67% - 25px)"
          position="absolute"
          variant="subtitle1"
          width="50px"
        >
          $1B
        </Typography>
      </div>
    </div>
  );
};

export const UnicornZone = memo(Component);
